import React, { useEffect, useState } from 'react';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import clsx from 'clsx';
import {
  Company, ForeignNational, PersonType, TeamMember, Employer,
} from 'API';
import CompanyService from 'services/company';
import NationalService from 'services/national';
import TeamService from 'services/team';
import PersonService from 'services/person';
import {
  alphaByName, getCacheWithExpiration, lower,
} from 'shared/utilities';
import { useDebounce } from 'use-debounce';
import { DICTIONARY, getCompanyOption } from 'shared/constants';

const { fullname, personTypes } = PersonService;
interface Props<T extends Company | Employer | ForeignNational | TeamMember> {
  className?: string;
  disabled?: boolean;
  elementId?: string;
  excludedIDs?: Array<string>;
  extraFilters?: string;
  label?: string;
  multiple?: boolean;
  onChange: (event: any, value: Array<T>) => void;
  required?: boolean;
  type: 'Company' | 'Employer' | 'ForeignNational' | 'TeamMember';
  value?: T | Array<T>;
  overrideSource?: Array<Employer | ForeignNational | TeamMember>;
  companyIds?: Array<string> | null | undefined;
  ignoreEnter?: boolean;
  helperText?: string;
  showDba?: boolean;
  optionFilter?: (option: any) => boolean;
  placeholder?: string;
  disableClearable?: boolean;
  /**
   * How many rows to pull from the search index. `optionFilter` runs on the
   * CLIENT, i.e. after this cut-off, so a field that filters out most matches
   * needs a wider window or eligible options fall off the end and become
   * unreachable. Defaults to the historical 20 for every other caller.
   */
  searchLimit?: number;
}

const AutocompleteWithSearch: React.FC<Props<
Company | Employer | ForeignNational | TeamMember>
> = ({
  className,
  disabled,
  elementId,
  excludedIDs,
  label,
  multiple,
  onChange,
  required,
  type,
  value,
  overrideSource,
  companyIds,
  ignoreEnter,
  helperText,
  showDba,
  optionFilter,
  placeholder,
  disableClearable,
  searchLimit = 20,
}: Props<Company | ForeignNational | Employer | TeamMember>) => {
  const [textInput, setTextInput] = useState('');
  const [hasInput, setHasInput] = useState(false);
  const [debouncedTextInput] = useDebounce(textInput, 200);
  const [isSearching, setIsSearching] = useState(false);
  const [options, setOptions] = useState<Array<Company | Person>>([]);

  const handleChange = (e: any, v: Array<any>) => {
    setTextInput('');
    onChange(e, v);
  };

  const handleTextChange = (event: any) => {
    setTextInput(event.target.value.trim());
  };

  useEffect(() => {
    let ignore = false;
    const cacheKey = `${label + type + textInput + searchLimit}autocompetesearch`;

    const load = async () => {
      let searchByName: SearchByName<Company | ForeignNational | TeamMember>;
      switch (type) {
        case 'Company':
          searchByName = CompanyService.searchByName;
          break;

        case 'ForeignNational':
          searchByName = NationalService.searchByName;
          break;

        case 'TeamMember':
          searchByName = TeamService.searchByName;
          break;

        default:
          break;
      }
      try {
        const response = await getCacheWithExpiration(
          cacheKey,
          () => searchByName(textInput, searchLimit),
          60 * 1000,
        );

        if (response && !ignore) {
          setOptions(
            (response as Array<any>).sort(alphaByName).filter(
              (item: Company | Person) => !(excludedIDs || []).includes(item.id)
                && (!optionFilter || optionFilter(item)),
            ),
          );
          setIsSearching(false);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err);
      }
    };
    setOptions([]);
    setIsSearching(true);
    setHasInput(textInput.length > 0);

    if (overrideSource) {
      setIsSearching(false);
      const source = overrideSource.filter((item: Person) => (
        item?.lowerFull?.includes(lower(debouncedTextInput))
          && (!optionFilter || optionFilter(item))
      ));
      setOptions(debouncedTextInput ? source : []);
    } else {
      load();
    }

    return () => {
      ignore = true;
    };
  }, [debouncedTextInput]);

  return (
    <Autocomplete
      disabled={disabled}
      disableClearable={disableClearable}
      filterOptions={(x) => x}
      id={elementId}
      className={clsx(className, multiple && 'autocomplete--chips')}
      multiple={multiple}
      onKeyPress={(e) => ((ignoreEnter && e.key === 'Enter') ? e.preventDefault() : null)}
      noOptionsText={
        isSearching
          ? DICTIONARY.AutocompleteSearching
          : hasInput
            ? DICTIONARY.NoOptions
            : DICTIONARY.AutocompletePrompt
      }
      options={
        companyIds?.length
          ? options.filter((opt) => companyIds.includes(opt.id))
          : options
      }
      getOptionLabel={(option) => `${
        personTypes.includes(type as PersonType)
          ? fullname(option)
          : showDba ? getCompanyOption(option)
            : ((option as Company).displayCompanyName || (option as Company).name)
      }`}
      isOptionEqualToValue={(option, v) => option.id === v.id}
      onChange={handleChange}
      value={Array.isArray(value) ? value.sort(alphaByName) : value || null}
      renderInput={(params) => (
        <strong>
          <TextField
            {...params}
            required={required}
            variant="standard"
            label={label}
            placeholder={placeholder}
            onChange={handleTextChange}
            helperText={helperText || ''}
            inputProps={{
              ...params.inputProps,
              autoComplete: 'new-password',
            }}
          />
        </strong>
      )}
      renderOption={({ key, ...props }, option) => (
        <Box
          component="li"
          key={key}
          {...props}
          sx={{ flexDirection: 'column', alignItems: 'flex-start !important' }}
        >
          <Typography>
            {personTypes.includes(type as PersonType)
              ? fullname(option)
              : showDba ? getCompanyOption(option)
                : ((option as Company).displayCompanyName || (option as Company).name)}
          </Typography>
          {option.email && (
            <Typography sx={{ fontSize: '0.75rem' }}>{option.email}</Typography>
          )}
        </Box>
      )}
    />
  );
};

export default AutocompleteWithSearch;
